import { router } from '@inertiajs/react'
import { createPortal } from 'react-dom'
import { useEffect, useMemo, useState } from 'react'

import { titleize } from '../../../utils/string'

function FlashAlert({ variant, message, onDismiss, textDark }) {
  if (!message) return null

  const className = [
    'alert',
    `alert-${variant}`,
    textDark ? 'text-dark' : '',
    'alert-dismissible',
    'fade',
    'show',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} role="alert">
      <h4 className="alert-heading mb-0">{message}</h4>
      <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close" onClick={onDismiss}></button>
    </div>
  )
}

function getDisplayName(user) {
  return user?.full_name || user?.email || ''
}

function formatBalance(value) {
  if (value == null) return '0'
  return Number(value).toFixed(2).replace(/\.00$/, '')
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function parseDate(value) {
  return new Date(`${value}T00:00:00`)
}

function formatKey(date) {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

function monthLabel(date) {
  return new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(date)
}

function buildCalendarDays(monthDate) {
  const firstDayOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1)
  const offset = (firstDayOfMonth.getDay() + 6) % 7
  const start = new Date(firstDayOfMonth)
  start.setDate(firstDayOfMonth.getDate() - offset)

  return Array.from({ length: 42 }, (_, index) => {
    const day = new Date(start)
    day.setDate(start.getDate() + index)
    return day
  })
}

function LeaveRequestOffcanvas({ request, onClose, onApprove, onDeny }) {
  if (!request) return null

  return createPortal(
    <>
      <div className="offcanvas-backdrop fade show" role="presentation" onClick={onClose} />
      <div className="offcanvas offcanvas-end w-50 show" tabIndex={-1} style={{ visibility: 'visible' }} role="dialog" aria-modal="true">
        <div className="offcanvas-header">
          <div className="flex-column">
            <h5 className="offcanvas-title mb-1">{request.employeeName}</h5>
            <div className="small text-body-secondary">{titleize(request.leave_type)} leave · {titleize(request.status)}</div>
          </div>
          <button type="button" className="btn-close text-reset" aria-label="Close" onClick={onClose} />
        </div>

        <div className="offcanvas-body">
          <div className="mb-3">
            <div className="small text-body-secondary">Dates</div>
            <div className="fw-semibold">{request.start_date_label}{request.start_date !== request.end_date ? ` - ${request.end_date_label}` : ''}</div>
          </div>
          <div className="mb-3">
            <div className="small text-body-secondary">Days</div>
            <div className="fw-semibold">{request.total_days}</div>
          </div>
          <div className="mb-3">
            <div className="small text-body-secondary">Balance Impact</div>
            <div>
              {request.leave_type === 'sick' ? 'Sick' : 'Vacation'}: {formatBalance(request.balance_before?.[request.leave_type])} → {formatBalance(request.balance_after?.[request.leave_type])}
            </div>
            {request.would_go_negative ? <div className="small text-warning">Balance will be negative.</div> : null}
          </div>
          <div className="mb-3">
            <div className="small text-body-secondary">Reason</div>
            <div>{request.reason || '—'}</div>
          </div>
          <div className="mb-3">
            <div className="small text-body-secondary">Turnover Plan</div>
            <div>{request.turnover_plan || '—'}</div>
          </div>
          <div className="mb-3">
            <div className="small text-body-secondary">Filed</div>
            <div>{request.created_at_label || '—'}</div>
          </div>
          <div className="mb-3">
            <div className="small text-body-secondary">Approved</div>
            <div>
              {request.approved_by ? `By ${request.approved_by.full_name || request.approved_by.email}` : 'Pending'}
              {request.approved_at_label ? ` · ${request.approved_at_label}` : ''}
            </div>
          </div>

          {request.status === 'pending' && request.can_approve ? (
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-primary" onClick={() => onApprove(request.id)}>
                Approve Leave Request
              </button>
              <button type="button" className="btn btn-outline-danger" onClick={() => onDeny(request.id)}>
                Deny Leave Request
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </>,
    document.body,
  )
}

export default function Index({ leave_requests, leave_refills, employees, can_manage_refills, can_manage_announcement, announcement_settings, notice, alert }) {
  const [activeTab, setActiveTab] = useState('overview')
  const [flashNotice, setFlashNotice] = useState(notice)
  const [flashAlert, setFlashAlert] = useState(alert)
  const [currentMonth, setCurrentMonth] = useState(() => new Date())
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [selectedEmployees, setSelectedEmployees] = useState([])
  const [refillForm, setRefillForm] = useState({
    refill_sick: false,
    refill_vacation: false,
    sick_amount: '',
    vacation_amount: '',
    effective_on: '',
    message: '',
  })
  const [announcementForm, setAnnouncementForm] = useState({
    daily_leave_announcement_enabled: announcement_settings?.daily_leave_announcement_enabled || false,
    telegram_leave_group_chat_id: announcement_settings?.telegram_leave_group_chat_id || '',
    telegram_leave_topic_id: announcement_settings?.telegram_leave_topic_id || '',
    telegram_leave_message: announcement_settings?.telegram_leave_message || '',
  })

  useEffect(() => {
    if (notice) setFlashNotice(notice)
    if (alert) setFlashAlert(alert)
  }, [notice, alert])

  const approve = (id) => {
    router.patch(`/managers/leave_approvals/${id}/approve`)
  }

  const deny = (id) => {
    router.patch(`/managers/leave_approvals/${id}/deny`)
  }

  const submitRefill = (event) => {
    event.preventDefault()

    router.post('/managers/leave_approvals/refill', {
      leave_refill: {
        user_ids: selectedEmployees,
        sick_amount: refillForm.refill_sick ? refillForm.sick_amount : null,
        vacation_amount: refillForm.refill_vacation ? refillForm.vacation_amount : null,
        effective_on: refillForm.effective_on || null,
        message: refillForm.message || null,
      },
    })
  }

  const submitAnnouncement = (event) => {
    event.preventDefault()

    router.post('/managers/leave_approvals/update_announcement', {
      company_setting: {
        daily_leave_announcement_enabled: announcementForm.daily_leave_announcement_enabled ? '1' : '0',
        telegram_leave_group_chat_id: announcementForm.telegram_leave_group_chat_id || null,
        telegram_leave_topic_id: announcementForm.telegram_leave_topic_id || null,
        telegram_leave_message: announcementForm.telegram_leave_message || null,
      },
    })
  }

  const sendTestAnnouncement = () => {
    router.post('/managers/leave_approvals/send_test_announcement')
  }

  const normalizedRequests = (leave_requests || []).map((request) => ({
    ...request,
    employeeName: getDisplayName(request.employee),
    startDate: parseDate(request.start_date),
    endDate: parseDate(request.end_date),
  }))

  const normalizedRefills = (leave_refills || []).map((refill) => ({
    ...refill,
    employeeName: getDisplayName(refill.employee),
    createdByName: getDisplayName(refill.created_by),
  }))

  const allSelected = useMemo(() => {
    if (!employees?.length) return false
    return selectedEmployees.length === employees.length
  }, [employees, selectedEmployees])

  const toggleAllEmployees = () => {
    if (allSelected) {
      setSelectedEmployees([])
    } else {
      setSelectedEmployees((employees || []).map((employee) => employee.id))
    }
  }

  const toggleEmployee = (id) => {
    setSelectedEmployees((list) => {
      if (list.includes(id)) return list.filter((value) => value !== id)
      return [...list, id]
    })
  }

  const dayCells = buildCalendarDays(currentMonth)
  const firstDayKey = formatKey(dayCells[0])
  const lastDayKey = formatKey(dayCells[dayCells.length - 1])
  const todayKey = formatKey(new Date())

  const requestsByDay = dayCells.reduce((accumulator, day) => {
    const dayKey = formatKey(day)
    accumulator[dayKey] = normalizedRequests.filter((request) => {
      const startKey = formatKey(request.startDate)
      const endKey = formatKey(request.endDate)
      return startKey <= dayKey && endKey >= dayKey
    })
    return accumulator
  }, {})

  const visibleRequests = normalizedRequests.filter((request) => {
    const startKey = formatKey(request.startDate)
    const endKey = formatKey(request.endDate)
    return !(endKey < firstDayKey || startKey > lastDayKey)
  })

  const pendingCount = visibleRequests.filter((request) => request.status === 'pending').length
  const approvedCount = visibleRequests.filter((request) => request.status === 'approved').length

  const moveMonth = (delta) => {
    setCurrentMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
  }

  return (
    <>
      <div className="container-fluid">
        <div className="header">
          <div className="header-body">
            <div className="row align-items-center">
              <div className="col">
                <h6 className="header-pretitle">HR</h6>
                <h1 className="header-title text-truncate">Leaves Workspace</h1>
              </div>
            </div>
            <div className="row align-items-center mt-3">
              <div className="col">
                <ul className="nav nav-tabs nav-overflow header-tabs">
                  <li className="nav-item">
                    <button type="button" className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                      Overview
                    </button>
                  </li>
                  <li className="nav-item">
                    <button type="button" className={`nav-link ${activeTab === 'approvals' ? 'active' : ''}`} onClick={() => setActiveTab('approvals')}>
                      Approvals
                    </button>
                  </li>
                  <li className="nav-item">
                    <button type="button" className={`nav-link ${activeTab === 'refills' ? 'active' : ''}`} onClick={() => setActiveTab('refills')}>
                      Refills
                    </button>
                  </li>
                  {can_manage_announcement ? (
                    <li className="nav-item">
                      <button type="button" className={`nav-link ${activeTab === 'announcement' ? 'active' : ''}`} onClick={() => setActiveTab('announcement')}>
                        Announcement
                      </button>
                    </li>
                  ) : null}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <FlashAlert variant="danger" message={flashAlert} onDismiss={() => setFlashAlert(null)} />
        <FlashAlert variant="success" message={flashNotice} textDark onDismiss={() => setFlashNotice(null)} />

        {activeTab === 'overview' ? (
          <div className="row g-4">
            <div className="col-12 col-xl-9">
              <div className="card">
                <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <div className="text-uppercase text-body-secondary fw-semibold small">Calendar</div>
                    <h4 className="card-header-title mb-0">{monthLabel(currentMonth)}</h4>
                  </div>
                  <div className="btn-group">
                    <button type="button" className="btn btn-sm btn-white" onClick={() => moveMonth(-1)}>Previous</button>
                    <button type="button" className="btn btn-sm btn-white" onClick={() => setCurrentMonth(new Date())}>Today</button>
                    <button type="button" className="btn btn-sm btn-white" onClick={() => moveMonth(1)}>Next</button>
                  </div>
                </div>

                <div className="card-body">
                  <div className="row g-2 mb-2">
                    {WEEKDAY_LABELS.map((label) => (
                      <div key={label} className="col text-center small text-body-secondary fw-semibold">{label}</div>
                    ))}
                  </div>

                  <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
                    {dayCells.map((day) => {
                      const dayKey = formatKey(day)
                      const dayRequests = requestsByDay[dayKey] || []
                      const inCurrentMonth = day.getMonth() === currentMonth.getMonth()
                      const shownRequests = dayRequests.slice(0, 3)
                      const hiddenCount = dayRequests.length - shownRequests.length
                      const isToday = dayKey === todayKey
                      const isWeekend = day.getDay() === 0 || day.getDay() === 6

                      return (
                        <div key={dayKey}>
                          <div className={`border rounded p-2 h-100 ${inCurrentMonth ? '' : 'bg-body-tertiary'} ${isToday ? 'border-primary border-2' : ''} ${isWeekend && inCurrentMonth ? 'bg-dark-subtle' : ''}`} style={{ minHeight: 170 }}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className={`small fw-semibold ${inCurrentMonth ? '' : 'text-body-secondary'}`}>{day.getDate()}</div>
                              {isToday ? <span className="badge text-bg-primary">Today</span> : null}
                            </div>

                            {shownRequests.map((request) => (
                              <button
                                key={`${dayKey}-${request.id}`}
                                type="button"
                                className={`btn btn-sm w-100 text-start mb-1 border ${request.status === 'approved' ? 'border-success-subtle bg-success-subtle' : 'border-warning-subtle bg-warning-subtle'}`}
                                onClick={() => setSelectedRequest(request)}
                              >
                                <div className="fw-semibold text-truncate">{request.employeeName}</div>
                                <div className="small text-truncate">{titleize(request.leave_type)} · {titleize(request.status)}</div>
                              </button>
                            ))}

                            {hiddenCount > 0 ? <div className="small text-body-secondary">+{hiddenCount} more</div> : null}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-3">
              <div className="card mb-4">
                <div className="card-header">
                  <h4 className="card-header-title mb-0">Summary</h4>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <div className="text-uppercase text-body-secondary fw-semibold small">Approved Leaves</div>
                    <div className="h2 mb-0">{approvedCount}</div>
                  </div>
                  <div className="mb-3">
                    <div className="text-uppercase text-body-secondary fw-semibold small">Pending Approvals</div>
                    <div className="h2 mb-0">{pendingCount}</div>
                  </div>
                  <div className="small text-body-secondary">Click an event on the calendar to view details and approve if needed.</div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h4 className="card-header-title mb-0">Legend</h4>
                </div>
                <div className="card-body">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span className="badge text-bg-success-subtle">Approved</span>
                    <span className="small text-body-secondary">Leave is approved.</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="badge text-bg-warning">Pending</span>
                    <span className="small text-body-secondary">Waiting for approval.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === 'approvals' ? (
          <div className="card">
            <div className="card-header">
              <h4 className="card-header-title mb-0">Leave Approvals</h4>
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-hover table-nowrap card-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Status</th>
                    <th>Type</th>
                    <th>Dates</th>
                    <th>Days</th>
                    <th>Balance Impact</th>
                    <th>Reason</th>
                    <th>Turnover Plan</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {normalizedRequests.map((request) => (
                    <tr key={request.id}>
                      <td>
                        <button type="button" className="btn btn-link p-0" onClick={() => setSelectedRequest(request)}>
                          {request.employeeName}
                        </button>
                      </td>
                      <td>
                        <span className={`badge ${request.status === 'approved' ? 'text-bg-success-subtle' : 'text-bg-warning'}`}>
                          {titleize(request.status)}
                        </span>
                      </td>
                      <td>{titleize(request.leave_type)}</td>
                      <td>{request.start_date_label}{request.start_date !== request.end_date ? ` - ${request.end_date_label}` : ''}</td>
                      <td>{request.total_days}</td>
                      <td>
                        <div>
                          {request.leave_type === 'sick' ? 'Sick' : 'Vacation'}: {formatBalance(request.balance_before?.[request.leave_type])} → {formatBalance(request.balance_after?.[request.leave_type])}
                        </div>
                        {request.would_go_negative ? <div className="small text-warning">Balance will be negative.</div> : null}
                      </td>
                      <td>{request.reason}</td>
                      <td>{request.turnover_plan}</td>
                      <td className="text-end">
                        {request.status === 'approved' ? (
                          <div className="small text-body-secondary text-end">
                            <div>Approved by {getDisplayName(request.approved_by) || 'Unknown'}</div>
                            <div>{request.approved_at_label || '—'}</div>
                          </div>
                        ) : request.status === 'pending' && request.can_approve ? (
                          <div className="d-flex justify-content-end gap-2">
                            <button type="button" className="btn btn-sm btn-primary" onClick={() => approve(request.id)}>
                              Approve
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => deny(request.id)}>
                              Deny
                            </button>
                          </div>
                        ) : (
                          <span className="text-body-secondary">No access</span>
                        )}
                      </td>
                    </tr>
                  ))}

                  {!normalizedRequests.length ? (
                    <tr>
                      <td colSpan={9} className="text-center text-body-secondary py-4">No leave requests to display.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}

        {activeTab === 'refills' ? (
          <div className="row g-4 align-items-start">
            <div className="col-12 col-xl-7">
              <div className="card h-100">
                <div className="card-header">
                  <h4 className="card-header-title mb-0">Refill History</h4>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-hover table-nowrap card-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Type</th>
                        <th className="text-end">Amount</th>
                        <th>Effective On</th>
                        <th>Initiated By</th>
                        <th>Created At</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {normalizedRefills.map((refill) => (
                        <tr key={refill.id}>
                          <td>{refill.employeeName}</td>
                          <td>{titleize(refill.leave_type)}</td>
                          <td className="text-end">{formatBalance(refill.amount)}</td>
                          <td>{refill.effective_on_label}</td>
                          <td>{refill.createdByName}</td>
                          <td>{refill.created_at_label}</td>
                          <td>{refill.message || '—'}</td>
                        </tr>
                      ))}

                      {!normalizedRefills.length ? (
                        <tr>
                          <td colSpan={7} className="text-center text-body-secondary py-4">No refills recorded yet.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="col-12 col-xl-5">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-header-title mb-0">Refill Credits</h4>
                </div>
                <div className="card-body">
                  {can_manage_refills ? (
                    <form onSubmit={submitRefill}>
                      <div className="form-group">
                        <div className="form-check mb-2">
                          <input id="refill-sick" type="checkbox" className="form-check-input" checked={refillForm.refill_sick} onChange={(event) => setRefillForm((form) => ({ ...form, refill_sick: event.target.checked }))} />
                          <label className="form-check-label" htmlFor="refill-sick">Refill Sick Leave</label>
                        </div>
                        <label className="form-label">Sick Leave (days)</label>
                        <input type="number" step="0.5" min="0" className="form-control" disabled={!refillForm.refill_sick} value={refillForm.sick_amount} onChange={(event) => setRefillForm((form) => ({ ...form, sick_amount: event.target.value }))} />
                      </div>

                      <div className="form-group">
                        <div className="form-check mb-2">
                          <input id="refill-vacation" type="checkbox" className="form-check-input" checked={refillForm.refill_vacation} onChange={(event) => setRefillForm((form) => ({ ...form, refill_vacation: event.target.checked }))} />
                          <label className="form-check-label" htmlFor="refill-vacation">Refill Vacation Leave</label>
                        </div>
                        <label className="form-label">Vacation Leave (days)</label>
                        <input type="number" step="0.5" min="0" className="form-control" disabled={!refillForm.refill_vacation} value={refillForm.vacation_amount} onChange={(event) => setRefillForm((form) => ({ ...form, vacation_amount: event.target.value }))} />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Effective On</label>
                        <input type="date" className="form-control" value={refillForm.effective_on} onChange={(event) => setRefillForm((form) => ({ ...form, effective_on: event.target.value }))} />
                        <small className="text-body-secondary">Leave blank to use today.</small>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Message (optional)</label>
                        <textarea className="form-control" rows={2} value={refillForm.message} onChange={(event) => setRefillForm((form) => ({ ...form, message: event.target.value }))} />
                      </div>

                      <div className="table-responsive mt-4 mb-3">
                        <table className="table table-sm table-hover table-nowrap">
                          <thead>
                            <tr>
                              <th style={{ width: 40 }}>
                                <input type="checkbox" className="form-check-input" checked={allSelected} onChange={toggleAllEmployees} />
                              </th>
                              <th>Employee</th>
                              <th className="text-end">Sick</th>
                              <th className="text-end">Vacation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(employees || []).map((employee) => (
                              <tr key={employee.id}>
                                <td>
                                  <input type="checkbox" className="form-check-input" checked={selectedEmployees.includes(employee.id)} onChange={() => toggleEmployee(employee.id)} />
                                </td>
                                <td>{getDisplayName(employee)}</td>
                                <td className="text-end">{formatBalance(employee.sick_leave_balance)}</td>
                                <td className="text-end">{formatBalance(employee.vacation_leave_balance)}</td>
                              </tr>
                            ))}

                            {!employees?.length ? (
                              <tr>
                                <td colSpan={4} className="text-center text-body-secondary py-4">No employees found.</td>
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>

                      <button type="submit" className="btn btn-primary" disabled={!selectedEmployees.length}>Refill Selected Employees</button>
                    </form>
                  ) : (
                    <div className="text-body-secondary">Only HR managers can create leave credit refills.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {activeTab === 'announcement' && can_manage_announcement ? (
          <div className="row g-4 align-items-start">
            <div className="col-12 col-xl-6">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-header-title mb-0">Daily Telegram Announcement</h4>
                </div>
                <div className="card-body">
                  <p className="text-body-secondary small">
                    Every weekday at 08:00 (GMT+8), the bot posts the leaves starting that day to your company's Telegram group. Days with no new leaves are skipped.
                  </p>
                  <form onSubmit={submitAnnouncement}>
                    <div className="form-group">
                      <div className="form-check">
                        <input
                          id="announcement-enabled"
                          type="checkbox"
                          className="form-check-input"
                          checked={announcementForm.daily_leave_announcement_enabled}
                          onChange={(event) => setAnnouncementForm((form) => ({ ...form, daily_leave_announcement_enabled: event.target.checked }))}
                        />
                        <label className="form-check-label" htmlFor="announcement-enabled">Enable daily announcement</label>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Telegram Group Chat ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={announcementForm.telegram_leave_group_chat_id}
                        onChange={(event) => setAnnouncementForm((form) => ({ ...form, telegram_leave_group_chat_id: event.target.value }))}
                        placeholder="-1001234567890"
                      />
                      <small className="text-body-secondary">Add the bot to your group, send /get_chat_id in the group, and paste the ID here.</small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Topic ID (optional)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={announcementForm.telegram_leave_topic_id}
                        onChange={(event) => setAnnouncementForm((form) => ({ ...form, telegram_leave_topic_id: event.target.value }))}
                        placeholder="Leave blank to post to General"
                      />
                      <small className="text-body-secondary">For topic-enabled groups: run /get_chat_id inside the target topic, or open the topic in Telegram Web — the number after the underscore in the URL is the topic ID.</small>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Message heading (optional)</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        value={announcementForm.telegram_leave_message}
                        onChange={(event) => setAnnouncementForm((form) => ({ ...form, telegram_leave_message: event.target.value }))}
                        placeholder="On Leave Today"
                      />
                      <small className="text-body-secondary">Defaults to "On Leave Today". The date and leave list are appended automatically.</small>
                    </div>

                    <div className="d-flex gap-2">
                      <button type="submit" className="btn btn-primary">Save Settings</button>
                      <button type="button" className="btn btn-white" onClick={sendTestAnnouncement}>Send Test Message</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <LeaveRequestOffcanvas request={selectedRequest} onClose={() => setSelectedRequest(null)} onApprove={approve} onDeny={deny} />
    </>
  )
}
